"use client";

import JoinCommunityButton from "@/modules/community/components/join-community-button";
import { TCommunityInfo } from "@/types/community";
import Image from "next/image";
import HeroSectionModerators from "./moderators";
import CommunityStats from "./stats";
import VerifiedBadgeCheck from "@/components/common/verified-badge-check";
import UpcomingEvent from "@/modules/community/components/calendar/components/community-upcoming-events";
import { useState } from "react";

const ExternalCommunityHero = ({ communityInfo }: { communityInfo: TCommunityInfo }) => {
	const [showFullDescription, setShowFullDescription] = useState(false);

	return (
		<div className="relative">
			<div className="relative aspect-[3/1] w-full overflow-hidden rounded-lg bg-brand-primary">
				<Image
					fill
					alt={communityInfo.name}
					src={communityInfo.coverImage || "/placeholder.svg"}
					className="object-cover"
					priority
				/>
			</div>

			<div className="relative">
				<div className="flex items-end gap-5">
					<div className="absolute -top-8 left-6">
						<div className="h-fit w-fit overflow-hidden rounded-xl bg-white p-1 shadow-lg">
							<Image
								className="h-24 w-24 rounded-xl 3xl:h-32 3xl:w-32"
								src={communityInfo.logo || "/placeholder.svg"}
								alt={communityInfo.name}
								width={1080}
								height={1080}
							/>
						</div>
					</div>

					<div className="flex flex-1 items-start justify-between gap-4 pt-20 max-xl:flex-wrap md:pl-36 md:pt-4 3xl:pl-44">
						<div className="flex flex-1 items-center justify-between max-md:flex-wrap">
							<div>
								<div className="mb-1 flex flex-wrap items-center gap-2 md:gap-3">
									<h1 className="max-w-lg text-xl font-bold text-gray-900 md:text-2xl 3xl:text-3xl">
										{communityInfo.name}
									</h1>

									<VerifiedBadgeCheck iconClassName="w-6 h-6 md:w-7 md:h-7" />
								</div>
							</div>

							<CommunityStats
								totalPosts={communityInfo.totalPosts}
								totalMembers={communityInfo.totalMembers}
								totalViews={communityInfo.totalViews}
							/>
						</div>
					</div>
				</div>
				<div className="w-full px-6 pt-2 text-sm text-gray-600 md:px-0 md:pl-36 3xl:pl-44 3xl:text-base">
					<p
						className={`${showFullDescription ? "" : "line-clamp-3"} whitespace-pre-line`}
						dangerouslySetInnerHTML={{ __html: communityInfo.description || "" }}
					/>
					{communityInfo.description && (
						<button
							className="mt-1 text-sm font-medium text-emerald-600"
							onClick={() => setShowFullDescription(!showFullDescription)}
						>
							{showFullDescription ? "Show less" : "Read more..."}
						</button>
					)}
				</div>

				<div className="mb-8 mt-3 flex items-center justify-between gap-2 max-md:flex-wrap 3xl:mt-6">
					<div className="w-full md:w-auto md:pl-6">
						<HeroSectionModerators moderators={communityInfo.moderators} />
					</div>

					<JoinCommunityButton communityId={communityInfo.id} />
				</div>
			</div>

			<div>
				<UpcomingEvent communitySlug={communityInfo.slug} />
			</div>
		</div>
	);
};

export default ExternalCommunityHero;
